import axios from 'axios';
import dayjs from 'dayjs';

class SubscriptionChecker {
  constructor() {
    this.processedSubscriptions = new Set();
    this.intervalId = null;
    this.isInitialized = false;
  }

  async initializeChecker() {
    if (this.isInitialized) {
      return; // Prevent multiple initializations
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found, subscription checker not initialized');
      return;
    }

    try {
      // Fetch user profile
      const profileResponse = await axios.get("https://admin.bizstor.lk/public/api/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const user = profileResponse.data;

      // Fetch subscriptions
      const subsResponse = await axios.get("https://admin.bizstor.lk/public/api/get-subscription", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allSubscriptions = subsResponse.data.subscription || [];
      const userSubscriptions = allSubscriptions.filter(
        (sub) => sub.email === user.email && sub.status === "active"
      );

      if (userSubscriptions.length > 0) {
        this.startPeriodicCheck(userSubscriptions);
        this.isInitialized = true;
        console.log('Subscription checker initialized successfully');
      }

    } catch (error) {
      console.error('Error initializing subscription checker:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        // Don't navigate here as this utility shouldn't control navigation
      }
    }
  }

  startPeriodicCheck(subscriptions) {
    // Clear any existing interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    // Initial check
    this.checkExpiredSubscriptions(subscriptions);

    // Set up periodic checking every minute
    this.intervalId = setInterval(() => {
      this.checkExpiredSubscriptions(subscriptions);
    }, 60000);
  }

  async checkExpiredSubscriptions(subscriptions) {
    if (!subscriptions || subscriptions.length === 0) {
      return;
    }

    const today = dayjs().format('YYYY-MM-DD');

    const subscriptionsToProcess = subscriptions.filter(sub => {
      const endDate = dayjs(sub.end_date).format('YYYY-MM-DD');
      const subscriptionKey = `${sub.id}-${today}`;

      return today >= endDate &&
        sub.status !== 'expire' &&
        !this.processedSubscriptions.has(subscriptionKey);
    });

    for (const sub of subscriptionsToProcess) {
      const subscriptionKey = `${sub.id}-${today}`;
      this.processedSubscriptions.add(subscriptionKey);

      try {
        const response = await axios.post('https://admin.bizstor.lk/public/api/subscription/expire', {
          email: sub.email
        });

        console.log(`Subscription expired for: ${sub.email}`, response.data);

      } catch (err) {
        console.error('Error expiring subscription:', err);
        // Remove from processed set if failed, so it can be retried
        this.processedSubscriptions.delete(subscriptionKey);
      }
    }
  }

  // Method to stop the checker (useful for cleanup)
  stopChecker() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isInitialized = false;
    this.processedSubscriptions.clear();
    console.log('Subscription checker stopped');
  }

  // Method to restart the checker with fresh data
  async restartChecker() {
    this.stopChecker();
    await this.initializeChecker();
  }
}

// Create a singleton instance
const subscriptionChecker = new SubscriptionChecker();

export default subscriptionChecker;